import cloneDeep from 'clone-deep'
import { type BaseFile, type InputType, COMPANY_NAME_FC, COMPANY_TITLE } from 'config'
import { DateTime } from 'luxon'
import type { ILoanGlobalSettings } from 'pages/Admin/AdminTools/Configuration/GlobalSettings'
import type { ILoan, ILoanDetail } from 'pages/LoanStructure/interfaces'
import { store } from 'reducers'

import { validateBankAccountNumber, validateRoutingNumber } from './bank'
import { getPrice3decimal, removeComma } from './convertor'

export const REQUIRED_FIELD_ERROR_MESSAGE = 'Required field'
export function validateEmail(email: string) {
  var re =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(String(email).toLowerCase())
}

export function isHTML(str: string) {
  var a = document.createElement('div')
  a.innerHTML = str

  for (var c = a.childNodes, i = c.length; i--; ) {
    if (c[i].nodeType == 1) return true
  }

  return false
}

function validateNumberLetters(val: string, count: number) {
  let numbers = 0
  val = val.toString()
  for (let i = 0; i < val.length; i += 1) {
    if (val[i] >= '0' && val[i] <= '9') numbers += 1
  }
  return numbers === count
}

function validCreditScore(val: string) {
  let numbers = 0
  val = val.toString()
  for (let i = 0; i < val.length; i += 1) {
    if (val[i] >= '0' && val[i] <= '9') numbers += 1
  }
  return numbers === 3
}

function validateUrl(val: string) {
  try {
    new URL(val)
    return true
  } catch (err) {
    return false
  }
}

/**
 * The window a date keyed into the platform may fall in.
 *
 * A native date input takes a two-digit year literally: typing 8/26/26 into one stores
 * `0026-08-26`, which is a perfectly well-formed date and passes any format check. On an
 * interest-bearing field that is money rather than cosmetics - a charge's Interest From Date of
 * 0026 quoted two thousand years of daily interest on a force-placed insurance charge. Dates are
 * bounded here, and on the inputs themselves, so a mis-keyed year is caught where it is typed.
 */
export const MIN_INPUT_DATE = '1900-01-01'
export const MAX_INPUT_DATE = '2199-12-31'

export const isValidDate = function (dateString: string) {
  try {
    var regEx = /^\d{4}-\d{2}-\d{2}$/
    if (!dateString.match(regEx)) return false // Invalid format
    var d = new Date(dateString)
    var dNum = d.getTime()
    if (!dNum && dNum !== 0) return false // NaN value, Invalid date
    return d.toISOString().slice(0, 10) === dateString
  } catch {
    return false
  }
}

export const isValidZipcode = function (value: string) {
  try {
    return /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(value)
  } catch {
    return false
  }
}

export function loanAmountRestriction(value: any, loanGlobalSettings: ILoanGlobalSettings) {
  const { minProposedLoanAmount, maxProposedLoanAmount } = loanGlobalSettings
  let error = isEmpty(value) ? REQUIRED_FIELD_ERROR_MESSAGE : ''

  if (
    removeComma(value) < removeComma(minProposedLoanAmount) ||
    removeComma(value) > removeComma(maxProposedLoanAmount)
  )
    error = `Must be in ${getPrice3decimal(minProposedLoanAmount)} - ${getPrice3decimal(maxProposedLoanAmount)}`

  return error
}

function asIsValueRestriction(value: any, loanGlobalSettings: ILoanGlobalSettings) {
  let error = isEmpty(value) ? REQUIRED_FIELD_ERROR_MESSAGE : ''
  value = removeComma(value)

  if (value < removeComma(loanGlobalSettings.minAsIsValue))
    return `Minimum value is ${getPrice3decimal(loanGlobalSettings.minAsIsValue)}`
  return error
}

export const InputValidate = function (
  data: any,
  loanDetail: ILoanDetail | undefined = undefined,
  loan: ILoan | undefined = undefined,
  rulesConfig: Record<string, any> | undefined = undefined,
) {
  let error = ''
  let {
    options,
    key = '',
    value = '',
    inputType,
    visible = true,
    required = false,
    readOnly = false,
    disabled = false,
    isPastDate = false,
    allowDefaultOption,
    maxLength = 0,
  } = data
  if (!visible) return ''
  if (!inputType) return ''
  if (disabled || readOnly) return ''
  if (['undefined', 'null'].indexOf(value) !== -1) value = null
  if (value && maxLength && value.length > maxLength) return `Maximum length must be ${maxLength}.`
  if (['section', 'group', 'divider', 'alert'].indexOf(inputType.toLowerCase()) !== -1) return ''

  if (inputType.toLowerCase() === 'multiselect' && data.maxSelected) {
    const selected = Array.isArray(value) ? value : Object.keys(value || {}).filter((key) => value[key])
    if (selected.length > data.maxSelected) return `You can select up to ${data.maxSelected} item(s).`
  }

  if (required) {
    if (isEmpty(value) || value.toString().length === 0) error = REQUIRED_FIELD_ERROR_MESSAGE
    if (inputType.toLowerCase() === 'select') {
      if (value === '') {
        if (allowDefaultOption) error = ''
        else error = REQUIRED_FIELD_ERROR_MESSAGE
      } else {
        const isCustomField = !!options.length && options[0]?.isCustom
        if (isCustomField) {
          if (!options.some((option: any) => option?.key === value)) error = REQUIRED_FIELD_ERROR_MESSAGE
        } else {
          if (Array.isArray(options) && options) {
            const inOptions = options.some((option: any) =>
              typeof option === 'object' ? option?.key === value || option?.title === value : option === value,
            )
            if (!inOptions) error = REQUIRED_FIELD_ERROR_MESSAGE
          } else if (!options || options[value] === undefined) error = REQUIRED_FIELD_ERROR_MESSAGE
        }
      }
    } else if (inputType.toLowerCase() === 'table') {
      if (value.length == 0) error = REQUIRED_FIELD_ERROR_MESSAGE
    } else if (inputType.toLowerCase() === 'multiselect') {
      const selected = Array.isArray(value) ? value : Object.keys(options).filter((key) => value[key] !== false)
      if (!selected.length) return REQUIRED_FIELD_ERROR_MESSAGE
    } else if (inputType.toLowerCase() === 'filetable') {
      if (!value.length) return REQUIRED_FIELD_ERROR_MESSAGE
    } else if (inputType.toLowerCase() === 'autocomplete' && data.type === 'email') {
      if (!Array.isArray(value) || !value.length) error = REQUIRED_FIELD_ERROR_MESSAGE
    } else if (inputType.toLowerCase() === 'searchableselect') {
      if (value == '0') error = REQUIRED_FIELD_ERROR_MESSAGE
    }
  } else if (value === null || value === undefined || value.toString().length === 0) return ''
  if (['estimatedCreditScore', 'proposedLoanAmount', 'amortizationType'].includes(key)) {
    if (!loanDetail) loanDetail = store.getState().loanDetail
    if (!loan) loan = store.getState().loan
  }
  switch (key) {
    case 'proposedMonthlyRent':
      if (removeComma(value) <= 0) error = `Must be above than 0`
      break
    case 'proposedOccupancy':
      if (value === 'Owner Occupied') error = `Can not be 'Owner Occupied'`
      break
    case 'proposedLoanAmount':
      error = loanAmountRestriction(value, loanDetail?.loanGlobalSettings as ILoanGlobalSettings)
      break
    case 'asIsValue':
      error = asIsValueRestriction(value, loanDetail?.loanGlobalSettings as ILoanGlobalSettings)
      break
    case 'howManyProperties':
      const { maxMultipleProperties } = loanDetail?.loanGlobalSettings as ILoanGlobalSettings
      if (value > maxMultipleProperties) error = `Must be less than ${maxMultipleProperties}`
      if (value < 2) error = `Must be >= 2`
      break
    case 'estimatedCreditScore':
      const { minCreditScore } = loanDetail?.loanGlobalSettings as ILoanGlobalSettings
      if (!loan?.isNoScore && removeComma(value) < removeComma(minCreditScore)) error = `Must be >= ${minCreditScore}`
      if (loan?.isNoScore) error = ''
      break
    case 'bankRoutingNumber':
      error = validateRoutingNumber(value)
      break
    case 'zipCode':
      if (!isValidZipcode(value)) error = `Invalid Code.`
      break
    // case 'amortizationType':
    //   if (isNonDSCRProduct(String(loan?.productType)) && loan?.amortizationType !== 'IO - Fixed')
    //     error = 'Must be IO - Fixed'
    //   break
    case 'numOfCoBorrowers':
      if (rulesConfig?.maximumBorrower && value > rulesConfig.maximumBorrower)
        error = `Must be less than or equal to ${rulesConfig.maximumBorrower}`
      break
  }
  if (error.length > 0) return error
  if (data.inputType.toLowerCase() === 'autocomplete') {
    if (data.type === 'email' && Array.isArray(value)) {
      const invalid = value.find((email) => !validateEmail(`${email}`))
      if (invalid !== undefined) error = 'Email address is not in a valid format.'
    }
    return error
  }
  try {
    switch (data.type) {
      case 'date':
        if (!isValidDate(data.value)) {
          error = 'Invalid Date Format.'
        } else if (data.value < MIN_INPUT_DATE || data.value > MAX_INPUT_DATE) {
          // Both sides are zero-padded YYYY-MM-DD by the check above, so comparing them as
          // strings orders them correctly.
          error = `Year must be between ${MIN_INPUT_DATE.slice(0, 4)} and ${MAX_INPUT_DATE.slice(0, 4)}.`
        } else if (isPastDate && new Date(data.value) > new Date()) error = 'This date cannot be a future date.'
        break
      case 'email':
        if (!validateEmail(data.value)) {
          error = 'Email address is not in a valid format.'
        }
        break
      case 'phone':
        if (!validateNumberLetters(data.value, 10)) {
          error = 'Must be 10 digits.'
        }
        break
      case 'ssn':
      case 'entityTaxID':
        if (!validateNumberLetters(data.value, 9)) {
          error = 'Must be 9 digits.'
        }
        break
      case 'routingNo':
        error = validateRoutingNumber(data.value)
        break
      case 'bankAccountNo':
        error = validateBankAccountNumber(data.value)
        break
      case 'mers':
        if (!validateNumberLetters(data.value, 18)) {
          error = 'Must be 18 digits.'
        }
        break
      case 'creditScore':
        if (!loan?.isNoScore && !validCreditScore(data.value)) {
          error = 'Must be 3 digits.'
        }
        break
      case 'url':
        if (!validateUrl(data.value)) {
          error = 'URL is not in a valid format.'
        }
        break
    }
  } catch {}
  return error
}

export const validDateMMDDYYYY = (date: string) => {
  let rlt = false
  try {
    const dt = DateTime.fromFormat(date, 'MM/dd/yyyy', { zone: 'local' })
    rlt = dt.isValid
  } catch {
    rlt = false
  }
  return rlt
}

export const isEmpty = (value: any): boolean =>
  value === undefined ||
  value === null ||
  (typeof value === 'object' && Object.keys(value).length === 0) ||
  (typeof value === 'string' && value.trim().length === 0)

export const isNull = (value: any): boolean => isNaN(value) || value === null

const keyMap: Record<string, string> = {
  accountExecutive: 'AccountExecutive',
  accountManager: 'AccountManager',
  broker: 'Broker',
  creator: 'Creator',
  loanOfficer: 'LoanOfficer',
  loanProcessor: 'LoanProcessor',
  underwriter: 'Underwriter',
}

export const getUnknownKeyInPartiesEmailTemplate = (content: string) => {
  const partyMaps = getPartiesMap({})
  while (true) {
    const matched = content.match(/{{.+?}}/)
    if (!matched) break
    const foundBlock = matched[0]
    const foundKey = foundBlock.replace(/{|}/g, '')
    if (partyMaps[foundKey] !== undefined) content = content.replace(foundBlock, partyMaps[foundKey])
    else return foundKey
  }
  return null
}

export const getPartiesMap = (parties: Record<string, any>) => {
  let partiesMap: Record<string, string> = {
    LTV: '',
    BorrowerCompanyName: '',
    BrokerStreet: '',
    BrokerCity: '',
    BrokerState: '',
    BrokerZipCode: '',
    LenderCompany: '',
    LenderStreet: '',
    LenderCity: '',
    LenderState: '',
    LenderZipCode: '',
    LoanTerm: '',
    DiscountFeePercent: '',
    DiscountFeeAmount: '',
    ExitFee: '',
    ConstructionBudget: '',
    ConstructionAmountFinanced: '',
    BrokerCompanyName: '',
    BorrowerType: '',
    FiveLaterDate: '',
    TodayDate: '',
    LoanOfficerOrBrokerName: '',
    EstimatedCreditScore: '',
    EstimatedClosingAmountToBorrower: '',
    EstimatedClosingAmountFromBorrower: '',
    MonthlyPaymentNonEscrow: '',
    MonthlyPaymentWithEscrow: '',
    RequiredReserves: '',
    PrepaymentPenalty: '',
    PurchasePrice: '',
    DSCRValue: '',
    InitialLoanAmount: '',
    LTC: '',
    LTARV: '',
    MonthlyPaymentInitialNonDutch: '',
    BorrowerEntityTitle: '',
    EntityName: '',
    BorrowerPresentAddress: '',
    AmortizationType: '',
    LoanNumber: '',
    SubjectPropertyAddress: '',
    BorrowerName: '',
    LoanProgram: '',
    Channel: '',
    LoanStatus: '',
    ProductType: '',
    InterestRate: '',
    LoanAmount: '',
    '120%LoanAmount': '',
    COMPANY: COMPANY_TITLE,
    COMPANY_TITLE,
    COMPANY_NAME_FC,
    AccountType: '',
    ClosingDate: '',
    AllGuarantors: '',
    AllPayoffs: '',
    VerifiedAssets: '',
    BorrowerEntityState: '',
    OriginalMaturityDate: '',
    CurrentMaturityDate: '',
    // Servicing extension terms, from the loan's latest extension. The extension emails pin these
    // to the specific extension they are about.
    ExtensionNumber: '',
    ExtensionFeeAmount: '',
    NewMaturityDate: '',
    // Why the borrower's last ACH came back. The NSF notice email pins these to its own reversal.
    NSFReasonCode: '',
    NSFReasonDescription: '',
    NSFReasonCodes: '',
    Mers: '',
    Broker: '',
    AppraisalStatus: '',
    WarehouseBank: '',
    WarehouseAdvanceRate: '',
    WarehouseFundingAmount: '',
    HaircutAmount: '',
    // Subject property, broken out of the one-line SubjectPropertyAddress
    PropertyStreet: '',
    PropertyCity: '',
    PropertyState: '',
    PropertyZipCode: '',
    PropertyCounty: '',
    PropertyType: '',
    PropertyCondoType: '',
    PropertyOccupancy: '',
    PropertyUnits: '',
    PropertyUnitNumber: '',
    PropertyParcelNumber: '',
    PropertyYearAcquired: '',
    PropertyAsIsValue: '',
    PropertyAfterRepairValue: '',
    PropertyPurchasePrice: '',
    PropertyMonthlyTaxes: '',
    PropertyMonthlyInsurance: '',
    PropertyMonthlyHoaDues: '',
    AdditionalPropertyAddresses: '',
    // Borrower ACH authorization (Servicing > ACH). Account numbers are masked on the backend.
    AchEnrolled: '',
    AchBankName: '',
    AchAccountName: '',
    AchAccountType: '',
    AchAccountLast4: '',
    AchAccountMasked: '',
    AchRoutingNumber: '',
    AchDraftAmount: '',
    AchDraftDayOfMonth: '',
    AchDelayDays: '',
    AchFirstPaymentDate: '',
    AchNextPaymentDate: '',
    AchPaymentFrequency: '',
    // Only filled in on a servicing notice email, which pins them to the notice it attaches: the
    // escalation level the borrower reached, and the figures that notice was generated against.
    NoticeLevel: '',
    NoticeDescription: '',
    NoticeAmountDue: '',
    NoticeDaysLate: '',
    BalloonMaturityDate: '',
    BalloonAmount: '',
    PolicyExpirationDate: '',
    // Only filled in on a payment-statement email, which pins them to the statement it attaches
    StatementPeriod: '',
    StatementPeriodStart: '',
    StatementPeriodEnd: '',
    StatementDate: '',
    StatementDueDate: '',
    StatementAmountDue: '',
  }
  Object.keys(keyMap).map((key) => {
    let { companyName, email, name, phone } = parties[key] || {}
    partiesMap[`${keyMap[key]}CompanyName`] = companyName || ''
    partiesMap[`${keyMap[key]}Name`] = name || ''
    partiesMap[`${keyMap[key]}Email`] = email || ''
    partiesMap[`${keyMap[key]}Phone`] = phone || ''
  })
  return partiesMap
}

export const isDateRangeIncludingToday = (fromDate: string, toDate: string) => {
  const today = DateTime.now().startOf('day')
  const fromDateTime = DateTime.fromISO(fromDate).startOf('day')
  const toDateTime = DateTime.fromISO(toDate).startOf('day')

  return today >= fromDateTime && today <= toDateTime
}

export const validateForm = ({ inputs, setInputs }: { inputs: Record<string, InputType>; setInputs: Function }) => {
  let hasError = false
  let newInputs = cloneDeep(inputs)
  const data: Record<string, any> = {}
  for (const key in inputs) {
    const input = newInputs[key]
    if (input.visible === false || (input.visible && typeof input.visible == 'function' && !input.visible(inputs)))
      continue

    if (input.value && input.value.trim) input.value = input.value.trim()
    input.error = InputValidate(input)
    data[key] = input.value
    if (input.error) hasError = true
    if (input.type == 'thousandSep') data[key] = removeComma(data[key])
  }

  if (hasError) setInputs(newInputs)

  return {
    hasError,
    data,
    newInputs,
  }
}

export const isBaseFile = (value: any): value is BaseFile => {
  return (
    value &&
    typeof value.id === 'number' &&
    typeof value.name === 'string' &&
    typeof value.fileKey === 'string' &&
    typeof value.createdAt === 'number'
  )
}

export const isPartyFilled = (party: any) => {
  const { FirstName, LastName, EMail, WorkPhone } = party
  return FirstName && LastName && EMail && WorkPhone
}
